import React, { useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import styled from 'styled-components';
import {
  FavoriteEntityType,
  IMeasureFieldsFragment,
  IVersionFieldsFragment,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import { EntityCategories } from '../../../components/Categories';
import { IApplyMeasureTags } from './types';
import EditableTextArea from '../../../components/EditableTextArea';
import {
  entityDescriptionSchema,
  entityNameSchema,
} from '../../../components/EditableTextArea/validation';
import { assertNotNil } from '../../../utils/types';
import { FavoriteVersionEntity } from '../../../components/Favorite';
import FormCreator from '../../pageForm/FormCreator';
import TimezonePopup from '../../../components/TimezonePopup';
import * as FormUI from '../../pageForm/FormUI';
import { useAppDispatch } from '../../../redux/hooks';
import BuilderInfo from '../../../components/BuilderInfo';
import ActionsMenu from '../../../components/ActionsMenu';
import PermissionElementWrapper from '../../PermissionControls/PermissionElementWrapper';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import Table from '../../../components/Table';
import { measureTableColumnsConfig } from './tableStylesConfig';
import { globalFormat } from '../../../utils/formatting';
import useSyndicateTimezoneLabel from '../../../utils/hooks/useSyndicateTimezoneLabel';
import FormEditor from '../../pageForm/FormEditor';
import EntityInfoPopover from '../../../components/EntityInfoPopover';
import { SingleSelectOption } from '../../../components/Dropdown/SingleSelect';
import {
  HeaderCheckbox,
  RowCheckbox,
} from '../../../components/Table/TableCheckbox';
interface IProps {
  namedMeasures: IMeasureFieldsFragment[];
  schemaId: string;
  onApplyTags: (args: IApplyMeasureTags) => Promise<void>;
  onChangeName: (version: IVersionFieldsFragment, newName: string) => void;
  onChangeDescription: (
    version: IVersionFieldsFragment,
    newDescription: string,
  ) => void;
  onSetFavorite: () => void;
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  isPermittedToAssignCategory: boolean;
}

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 2px;
`;

const initialState = {
  sorting: [
    {
      id: 'name',
      desc: true, // sort by name in descending order by default
    },
  ],
};

const columnHelper = createColumnHelper<IMeasureFieldsFragment>();

const MeasuresTable = ({
  namedMeasures,
  schemaId,
  rowSelection,
  setRowSelection,
  onApplyTags,
  onChangeName,
  onChangeDescription,
  onSetFavorite,
  isPermittedToAssignCategory,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const shortTimeZone = useSyndicateTimezoneLabel();

  const { checkIsPermitted } = usePermission();
  const isPermittedToDisableNamedMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureDelete],
    schemaId,
  });
  const isPermittedToEditNamedMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureEdit],
    schemaId,
  });

  const handleOpenDeleteMeasureModal = useCallback(
    (measure: IMeasureFieldsFragment) => {
      dispatch(
        openModal({ type: ModalSliceType.DeleteMeasure, data: measure }),
      );
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'checkbox',
        header: ({ table }) => <HeaderCheckbox table={table} />,
        cell: (info) => <RowCheckbox row={info.row} />,
        meta: measureTableColumnsConfig.checkbox,
      }),
      columnHelper.accessor('version', {
        id: 'name',
        header: 'Name',
        cell: (info) => {
          const version = info.getValue();
          assertNotNil(version);

          return (
            <EditableTextArea
              isBold
              disabled={version.isDisabled}
              placeholder="Add Name..."
              value={version.name}
              onChange={(newValue) => onChangeName(version, newValue)}
              defaultRows={2}
              validation={entityNameSchema}
              isReadOnly={!isPermittedToEditNamedMeasure}
            />
          );
        },
        meta: measureTableColumnsConfig.name,
        enableSorting: true,
      }),
      columnHelper.accessor('version', {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const version = info.getValue();
          assertNotNil(version);

          return (
            <EditableTextArea
              disabled={version.isDisabled}
              placeholder={'Add Description...'}
              value={version.description}
              onChange={(newValue) => onChangeDescription(version, newValue)}
              validation={entityDescriptionSchema}
              isReadOnly={!isPermittedToEditNamedMeasure}
            />
          );
        },
        meta: measureTableColumnsConfig.description,
        enableSorting: true,
      }),
      columnHelper.accessor('version', {
        id: 'tags',
        header: 'Tags',
        cell: (info) => {
          const categories = info.row.original.categories;

          return (
            <EntityCategories
              schemaId={schemaId}
              isDisabled={!isPermittedToAssignCategory}
              entityCategories={categories}
              onApply={(newTags, isManaged) =>
                onApplyTags({
                  entities: [info.row.original],
                  newTags,
                  isManaged,
                  schemaId,
                })
              }
              isManaged={info.row.original.isManaged}
            />
          );
        },
        meta: measureTableColumnsConfig.tags,
        enableSorting: true,
      }),
      columnHelper.accessor('isFavorite', {
        id: 'favorite',
        header: 'Favorite',
        cell: (info) => {
          const isFavorite = info.getValue();
          const id = info.row.original.id;

          return (
            <FavoriteVersionEntity
              entityType={FavoriteEntityType.Measure}
              entity={{ id, isFavorite }}
              onRefresh={onSetFavorite}
            />
          );
        },
        meta: measureTableColumnsConfig.favorite,
        enableSorting: true,
      }),
      columnHelper.accessor('version.creator', {
        id: 'creator',
        header: 'Creator',
        cell: (info) => {
          const creator = info.getValue();

          return (
            <FormCreator
              label={creator.name}
              picture={creator.picture}
              createdAt={info.row.original.version?.createdAt}
            />
          );
        },
        meta: measureTableColumnsConfig.creator,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          assertNotNil(rowA.original.version);
          assertNotNil(rowB.original.version);
          const creatorNameA = rowA.original.version.creator.name;
          const creatorNameB = rowB.original.version.creator.name;

          return creatorNameA > creatorNameB
            ? 1
            : creatorNameA < creatorNameB
              ? -1
              : 0;
        },
      }),
      columnHelper.accessor('version.editor', {
        id: 'editor',
        header: 'Last Edited',
        cell: (info) => {
          const editor = info.getValue();

          return (
            <FormEditor
              label={editor.name}
              picture={editor.picture}
              lastEdited={info.row.original.version?.editedAt}
            />
          );
        },
        meta: measureTableColumnsConfig.editor,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          assertNotNil(rowA.original.version);
          assertNotNil(rowB.original.version);
          const editorNameA = rowA.original.version.editor.name;
          const editorNameB = rowB.original.version.editor.name;

          return editorNameA > editorNameB
            ? 1
            : editorNameA < editorNameB
              ? -1
              : 0;
        },
      }),
      columnHelper.accessor('version.editedAt', {
        id: 'edited-time',
        header: `Last Edit Time (${shortTimeZone})`,
        cell: (info) => {
          const editedAt = info.getValue();

          return (
            <FormUI.DataLabel>
              <TimezonePopup
                timestamp={editedAt as string}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                placement="bottom-end"
                showTimezone
              />
            </FormUI.DataLabel>
          );
        },
        meta: measureTableColumnsConfig.creationTime,
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const measure = info.row.original;

          return (
            <ActionsContainer>
              <EntityInfoPopover closeOnAncestorScroll={true}>
                <BuilderInfo kind="namedMeasure" measure={measure} />
              </EntityInfoPopover>
              <ActionsMenu>
                <PermissionElementWrapper
                  isPermitted={isPermittedToDisableNamedMeasure}
                >
                  <SingleSelectOption
                    onClick={() => handleOpenDeleteMeasureModal(measure)}
                    iconName="trash"
                    label={'Delete'}
                    disabled={!isPermittedToDisableNamedMeasure}
                  />
                </PermissionElementWrapper>
              </ActionsMenu>
            </ActionsContainer>
          );
        },
        meta: measureTableColumnsConfig.actions,
      }),
    ],
    [
      dateFormat,
      timeFormat,
      handleOpenDeleteMeasureModal,
      isPermittedToAssignCategory,
      isPermittedToDisableNamedMeasure,
      isPermittedToEditNamedMeasure,
      onApplyTags,
      onChangeDescription,
      onChangeName,
      schemaId,
      shortTimeZone,
      onSetFavorite,
    ],
  );

  const table = useReactTable({
    data: namedMeasures,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    initialState,
    // Ensures each row has a unique identifier based on the `versionId` of the measure's version.
    getRowId: (row) => {
      assertNotNil(row.version);
      return row.version.versionId;
    },
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
    />
  );
};

export default MeasuresTable;
